import React, { useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'

import { applicationPost } from '../../../api/senecApiUtils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setCustomerValuesOverviewSlice } from '../../../store/slices/customerInputValuesSlice'
import { customerValuesType } from '../../../types'
import { createPostBody } from '../../../utils/helpers'
import { FormCheck } from '../../molecules'
import { OverviewSection } from '../../organisms'
import ContractOffers from '../../organisms/ContractOffers'

type OverviewProps = {
  nextPage: string,
  prevPage: string,
  changeActivePage: (pageUrl: string) => void
}

const Component = ({ nextPage, prevPage, changeActivePage }: OverviewProps) => {
  const [submitWasClicked, setSubmitWasClicked] = useState<boolean>(false)

  const customerInputValues = useAppSelector(state => state.customervalues)
  const packageProposalData = useAppSelector(state => state.contract)
  const productDetails = useAppSelector(state => state.productdetail)
  const electricityGridOperator = useAppSelector(state => state.electricitygridoperator)
  const dispatch = useAppDispatch()

  const { register, handleSubmit, getValues, formState: { errors } } = useForm<customerValuesType.Overview>({
    mode: 'onChange',
    defaultValues: { ...customerInputValues['p-overview'] }
  })

  const handleBack = () => {
    const formData = getValues()
    dispatch(setCustomerValuesOverviewSlice(formData))
    changeActivePage(prevPage)
  }

  const onSubmitFn: SubmitHandler<customerValuesType.Overview> = async (data) => {
    try {
      setSubmitWasClicked(true)
      dispatch(setCustomerValuesOverviewSlice(data))
      const mergedInputValues = { ...customerInputValues, 'p-overview': { ...data } }

      const body = createPostBody(mergedInputValues, productDetails, packageProposalData, electricityGridOperator)
      await applicationPost(body)

      changeActivePage(nextPage)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  return (
    <div className="react-form__page react-form__page--overview relative">
      {submitWasClicked && (
        <div className="react-form__overview-overlay absolute top-0 bottom-0 left-0 right-0 z-10"/>
      )}
      <div className="react-form__page__header relative bg-light md:p-6 md:p-10 xl:p-16">
        <div className="react-form__page__intro-text text-center pb-10">
          <h1 className="h2">Auftrag für Ihre SENEC.Cloud</h1>
          <p>
            Nehmen Sie sich einen Moment Zeit und überprüfen Sie Ihre erfassten Daten.<br/>
            Die Sonne zahlt dann schon bald Ihre Stromrechnung.
          </p>
        </div>
        <OverviewSection/>
      </div>
      <ContractOffers/>
      <div className="bg-gray-lighter md:p-6 md:p-10 xl:p-16 no-star-label">
        <h2 className="h3 mb-10">
          Beauftragung, Einverständnis und Vollmachten
        </h2>
        <FormCheck
          id="overview-agb"
          label={<div className='label-required'>Ich habe die Informationen auf den vorangegangenen Seiten 1 bis 6
            dieser Vertragsstrecke sowie die <a
              href='/anlage/AllgemeineGeschäftsbedingungen.pdf' target='_blank'>Allgemeinen
              Geschäftsbedingungen</a> der Cloud gelesen und akzeptiert.
            <p> Bitte beachten Sie auch die <a
              href='/anlage/Widerrufsbelehrung.pdf' target='_blank'>Widerrufsbelehrung</a> zu Ihrem Vertrag.
            </p>
          </div>}
          register={register}
          errors={errors}
          validations={{
            required: 'Bitte füllen Sie dieses Feld aus.'
          }}
        />
        <FormCheck
          id="overview-vertrag-kuendigen"
          label={<div><p>Ja, ich beauftrage die SENEC GmbH („SENEC“), Saarländer Str. 25, 04179 Leipzig, für meine
            genannten Zähler und in diesem Vertrag aufgeführten Abnahmestellen mit der Belieferung mit Strom.</p><br/>
            <p className='font-weight-bold'>Ich erteile folgende Vollmachten:</p><p>SENEC darf den bestehenden
              Stromliefervertrag für meinen Haushaltsstrom und im Falle der Auswahl des Zusatzprodukts „Wärme“ den
              bestehenden Stromliefervertrag für Wärmestrom und im Falle der Auswahl des Zusatzprodukts „Family and
              Friends“ die bestehenden Stromlieferverträge für die angegebenen Abnahmestellen zum nächstmöglichen
              Termin kündigen. Dies erleichtert die Durchführung des Lieferantenwechsels.</p><p
              className='label-required'>Ich bevollmächtige SENEC zur Vornahme aller Handlungen sowie Abgabe und
              Entgegennahme aller Erklärungen, die im Zusammenhang mit dem Wechsel des Stromversorgers erforderlich
              werden, etwa die Abfrage der Vorjahresverbrauchsdaten, soweit dem Kunden dadurch keine Kosten entstehen.
              Soweit und solange ein Dritter für den Messstellenbetrieb inklusive Messung zuständig ist,
              bevollmächtige ich SENEC auch zur Abfrage der Messwerte bei diesem Dritten.</p></div>}
          register={register}
          errors={errors}
          validations={{
            required: 'Bitte füllen Sie dieses Feld aus.'
          }}
        />
        <FormCheck
          id="overview-kenntnis-genommen"
          label={
            <div>
              <p className='font-weight-bold'>Das habe ich zur Kenntnis genommen:</p>
              <p className='label-required'>Die beigefügten Anlagen sind neben diesem Auftrag wesentlicher
                Bestandteil des Vertrags. Ich bestätige mit meiner Unterschrift deren Erhalt sowie den von mir
                abgegebenen Auftrag für die SENEC.Cloud und erkläre mich jeweils damit einverstanden.</p>
              <a
                href='/anlage/SEPALastschriftmandat.pdf'
                target='_blank'>SEPA-Lastschriftmandat</a><br/>
              <a
                href='/anlage/Widerrufsformular.pdf'
                target='_blank'>Muster-Widerrufsformular</a><br/>
              <a
                href='/anlage/AllgemeineGeschäftsbedingungen.pdf'
                target='_blank'>Allgemeine Geschäftsbedingungen</a><br/>
            </div>
          }
          register={register}
          errors={errors}
          validations={{
            required: 'Bitte füllen Sie dieses Feld aus.'
          }}
        />
        <FormCheck
          id="overview-bestens-informiert"
          label={
            'BESTENS INFORMIERT (freiwillig): Ja, die SENEC, Saarländer Str. 25, 04179 Leipzig, ' +
            'darf mich zukünftig per Telefon, E-Mail und SMS über eigene Angebote aus den Bereichen Strom, Wärme, ' +
            'energienahe Dienstleistungen, Photovoltaik und E-Mobilität werblich kontaktieren und meinen Namen, ' +
            'meine Telefonnummer und meine E- Mail-Adresse zum Zweck der Kontaktaufnahme verarbeiten. Die Erteilung ' +
            'der Einwilligung ist freiwillig und ich kann sie jederzeit telefonisch (Tel. +49 (0) 341 9898 2400), ' +
            'schriftlich (SENEC GmbH, Saarländer Str. 25, 04179 Leipzig) oder per E-Mail (strom@senec.com), ganz ' +
            'oder in Teilen widerrufen, ohne dass die Rechtmäßigkeit der bisherigen Datenverarbeitung berührt wird.'
          }
          register={register}
          errors={errors}
        />
        {Object.keys(errors).length > 0 && (
          <div className="mt-2 text-sm form-item--error-message">
            Nicht alle Pflichtfelder auf dieser Seite wurden akzeptiert
          </div>)
        }
        <div className="flex justify-between bg-gray-lighter px-5 pb-10">
          {prevPage && (
            <qz-button
              className="pb-4 flex-none"
              onClick={handleBack}
            >
              Zurück
            </qz-button>
          )}
          {nextPage && (
            <qz-button
              onClick={handleSubmit(onSubmitFn)}
              className="flex-none"
              buttonType="submit"
            >
              Jetzt&nbsp;
              <br className="sm:hidden"/>
              zahlungspflichtig&nbsp;
              <br className="sm:hidden"/>
              bestellen
            </qz-button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Component
